import type { Identifier, RaRecord } from 'react-admin';

import { readProjectSettings } from './adminSettings';
import { readAdminSession } from './authProvider';
import {
    BILLING_COUNTRY_CURRENCIES_RESOURCE,
    BILLING_FEATURES_RESOURCE,
    BILLING_PLAN_FEATURES_RESOURCE,
    BILLING_PLANS_RESOURCE,
    getBillingFeatureLabel,
    normalizeBillingPlan,
    type BillingCountryCurrency,
    type BillingFeature,
    type BillingFeatureDataType,
    type BillingPlan,
    type BillingPlanFeature,
    type BillingPlanPrice,
} from '../billing/billingData';

type AdminSubscriptionResource =
    | typeof BILLING_FEATURES_RESOURCE
    | typeof BILLING_PLANS_RESOURCE
    | typeof BILLING_PLAN_FEATURES_RESOURCE
    | typeof BILLING_COUNTRY_CURRENCIES_RESOURCE;

export type AdminUserActiveSubscription = {
    id: string;
    userId: string;
    planId: string;
    planName: string;
    status: string;
    isActive: boolean;
    startsAt: string;
    endsAt: string;
    updatedAt: string;
    raw: Record<string, unknown>;
};

export type AdminPricingPromotionChangeCount = {
    insert: number;
    update: number;
    deactivate: number;
    remove: number;
};

export type AdminPricingPromotionResult = {
    sourceEnvironment: string;
    targetEnvironment: string;
    canPublish: boolean;
    published: boolean;
    publishedAtUtc?: string | null;
    blockers: string[];
    warnings: string[];
    changes: Record<string, AdminPricingPromotionChangeCount>;
    planImagesToCopy: number;
    planImagesCopied: number;
};

type AdminApiEnvelope<T> = {
    success?: boolean;
    data?: T;
    statusCode?: number;
    errorMessage?: string | null;
    message?: string;
    error?: string;
    title?: string;
    detail?: string;
};

const adminSubscriptionResources = new Set<string>([
    BILLING_FEATURES_RESOURCE,
    BILLING_PLANS_RESOURCE,
    BILLING_PLAN_FEATURES_RESOURCE,
    BILLING_COUNTRY_CURRENCIES_RESOURCE,
]);

export function isAdminSubscriptionResource(resource: string): resource is AdminSubscriptionResource {
    return adminSubscriptionResources.has(resource);
}

function trimTrailingSlash(value: string): string {
    return value.replace(/\/+$/, '');
}

function trimSlashes(value: string): string {
    return value.replace(/^\/+|\/+$/g, '');
}

function hasVersionedApiPath(value: string): boolean {
    return /\/api\/v[^/]+$/i.test(trimTrailingSlash(value));
}

function getAdminApiConfig() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const baseUrl = trimTrailingSlash(
        String(
            import.meta.env.VITE_API_BASE_URL ??
                import.meta.env.VITE_API_URL ??
                settings.adminApiBaseUrl ??
                'https://test-api.pteultra.com/api/v1',
        ),
    );
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const adminToken = String(
        session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '',
    );

    return { baseUrl, apiVersion, adminToken };
}

function getAdminAssetBaseUrl(): string {
    const fileBaseUrl = trimTrailingSlash(String(import.meta.env.VITE_BASE_FILE_URL ?? ''));
    if (fileBaseUrl) {
        return fileBaseUrl;
    }

    const { baseUrl } = getAdminApiConfig();
    if (hasVersionedApiPath(baseUrl)) {
        return trimTrailingSlash(baseUrl.replace(/\/api\/v[^/]+$/i, ''));
    }

    return trimTrailingSlash(baseUrl);
}

function resolveAdminAssetUrl(value: unknown): string {
    const rawValue = String(value ?? '').trim();
    if (!rawValue) return '';

    if (/^https?:\/\//i.test(rawValue)) {
        return rawValue;
    }

    return new URL(rawValue.replace(/^\/+/, ''), `${getAdminAssetBaseUrl()}/`).toString();
}

function getErrorMessage(payload: unknown, fallback: string): string {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload && typeof payload === 'object') {
        const candidate = payload as Record<string, unknown>;
        const direct =
            candidate.errorMessage ?? candidate.message ?? candidate.error ?? candidate.title ?? candidate.detail;
        if (typeof direct === 'string' && direct.trim()) return direct;
    }
    return fallback;
}

function unwrapAdminApiPayload<T>(payload: T | AdminApiEnvelope<T>): T {
    if (!payload || typeof payload !== 'object' || !('data' in payload)) {
        return payload as T;
    }

    const envelope = payload as AdminApiEnvelope<T>;

    if (envelope.success === false) {
        throw new Error(getErrorMessage(envelope, 'Admin subscription request failed'));
    }

    if (envelope.data !== undefined) {
        return envelope.data;
    }

    return payload as T;
}

async function requestAdminSubscription<T>(
    path: string,
    init: RequestInit = {},
    query?: Record<string, string>,
): Promise<T> {
    const { baseUrl, apiVersion, adminToken } = getAdminApiConfig();
    const apiRoot = hasVersionedApiPath(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
    const url = new URL(`${apiRoot}/AdminSubscription/${trimSlashes(path)}`);

    for (const [key, value] of Object.entries(query ?? {})) {
        if (value !== '') {
            url.searchParams.set(key, value);
        }
    }

    const headers = new Headers(init.headers);
    if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json');
    }
    if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }
    if (adminToken && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${adminToken}`);
    }

    const response = await fetch(url.toString(), {
        ...init,
        headers,
    });

    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('application/json') ? await response.json() : await response.text();

    if (!response.ok) {
        throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    }

    if (payload && typeof payload === 'object' && 'success' in (payload as Record<string, unknown>)) {
        const envelope = payload as AdminApiEnvelope<unknown>;
        if (envelope.success === false) {
            throw new Error(getErrorMessage(envelope, 'Admin subscription request failed'));
        }
    }

    return payload as T;
}

function isNotFoundError(error: unknown): boolean {
    if (!(error instanceof Error)) {
        return false;
    }

    const message = error.message.trim().toLowerCase();
    return message.startsWith('404') || message.includes('not found');
}

async function requestAdminSubscriptionOrNull<T>(
    path: string,
    init: RequestInit = {},
    query?: Record<string, string>,
): Promise<T | null> {
    try {
        return await requestAdminSubscription<T>(path, init, query);
    } catch (error) {
        if (isNotFoundError(error)) {
            return null;
        }

        throw error;
    }
}

export async function uploadAdminSubscriptionPlanImage(planId: Identifier, file: File) {
    const formData = new FormData();
    formData.append('file', file);

    return requestAdminSubscription<unknown>(`plans/${String(planId)}/image`, {
        method: 'POST',
        body: formData,
    });
}

function getArrayFromPayload<T>(payload: unknown): T[] {
    const unwrapped = unwrapAdminApiPayload(payload as T[] | AdminApiEnvelope<T[]>);

    if (Array.isArray(unwrapped)) {
        return unwrapped;
    }

    if (unwrapped && typeof unwrapped === 'object') {
        const candidate = unwrapped as Record<string, unknown>;
        if (Array.isArray(candidate.items)) {
            return candidate.items as T[];
        }
    }

    return [];
}

function matchesFilter(record: Record<string, unknown>, filter: Record<string, unknown>): boolean {
    const entries = Object.entries(filter ?? {});
    if (entries.length === 0) return true;

    return entries.every(([key, value]) => {
        if (value === undefined || value === null || value === '') return true;
        const recordValue = record[key];
        if (recordValue === undefined || recordValue === null) return false;
        return String(recordValue).toLowerCase().includes(String(value).toLowerCase());
    });
}

function compareValues(a: unknown, b: unknown): number {
    if (a === b) return 0;
    if (a === undefined || a === null) return -1;
    if (b === undefined || b === null) return 1;
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b));
}

function sortAndPaginate<T extends RaRecord>(records: T[], params: any) {
    const filter = (params.filter ?? {}) as Record<string, unknown>;
    const filtered = records.filter((record) => matchesFilter(record as Record<string, unknown>, filter));

    const { field, order } = params.sort ?? { field: 'id', order: 'ASC' };
    const sorted = [...filtered].sort((a, b) => {
        const cmp = compareValues((a as Record<string, unknown>)[field], (b as Record<string, unknown>)[field]);
        return order === 'DESC' ? -cmp : cmp;
    });

    const { page, perPage } = params.pagination ?? { page: 1, perPage: 25 };
    const start = (page - 1) * perPage;
    const end = start + perPage;

    return {
        data: sorted.slice(start, end),
        total: sorted.length,
    };
}

function nowIso(): string {
    return new Date().toISOString();
}

function parseBillingFeatureDataType(value: unknown): BillingFeatureDataType {
    if (typeof value === 'string') {
        switch (value.trim().toLowerCase()) {
            case 'bool':
            case 'boolean':
                return 1;
            case 'int':
            case 'integer':
                return 2;
            case 'string':
            case 'text':
                return 3;
        }
    }

    const numericValue = Number(value);
    if (numericValue === 1 || numericValue === 2 || numericValue === 3) {
        return numericValue;
    }

    return 3;
}

function getFirstString(record: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
        const value = record[key];
        if (typeof value === 'string' && value.trim()) {
            return value.trim();
        }

        if (typeof value === 'number' && Number.isFinite(value)) {
            return String(value);
        }
    }

    return '';
}

function getFirstBoolean(record: Record<string, unknown>, keys: string[]): boolean | undefined {
    for (const key of keys) {
        const value = record[key];
        if (typeof value === 'boolean') {
            return value;
        }

        if (typeof value === 'string') {
            const normalized = value.trim().toLowerCase();
            if (normalized === 'true') return true;
            if (normalized === 'false') return false;
        }
    }

    return undefined;
}

function getFirstObject(record: Record<string, unknown>, keys: string[]): Record<string, unknown> | undefined {
    for (const key of keys) {
        const value = record[key];
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            return value as Record<string, unknown>;
        }
    }

    return undefined;
}

function normalizeAdminUserActiveSubscription(item: unknown): AdminUserActiveSubscription {
    const record = ((item && typeof item === 'object' ? item : {}) as Record<string, unknown>);
    const userRecord = getFirstObject(record, ['user', 'appUser', 'applicationUser']) ?? {};
    const planRecord = getFirstObject(record, ['plan', 'subscriptionPlan']) ?? {};

    const userId =
        getFirstString(record, ['userId', 'appUserId', 'applicationUserId']) ||
        getFirstString(userRecord, ['userId', 'appUserId', 'applicationUserId', 'id']);
    const planId =
        getFirstString(record, ['planId', 'targetPlanId', 'subscriptionPlanId']) ||
        getFirstString(planRecord, ['planId', 'id']);
    const planName = getFirstString(record, ['planName']) || getFirstString(planRecord, ['name', 'planName']);
    const status =
        getFirstString(record, ['status', 'subscriptionStatus', 'state']) ||
        (getFirstBoolean(record, ['isActive', 'active']) === false ? 'Inactive' : 'Active');
        const isActive =
            getFirstBoolean(record, ['isActive', 'active']) ?? (/^active$/i.test(status) || status === '');
    const startsAt = getFirstString(record, ['startsAt', 'startAt', 'activatedAt', 'effectiveFrom', 'createdAt']);
    const endsAt = getFirstString(record, ['endsAt', 'endAt', 'expiresAt', 'expiryDate', 'effectiveTo']);
    const updatedAt = getFirstString(record, ['updatedAt', 'lastModifiedAt', 'modifiedAt', 'createdAt']);
    const id = getFirstString(record, ['subscriptionId', 'id']) || `${userId || 'user'}:${planId || 'active'}`;

    return {
        id,
        userId,
        planId,
        planName,
        status,
        isActive,
        startsAt,
        endsAt,
        updatedAt,
        raw: record,
    };
}

function normalizeFeature(item: unknown): BillingFeature {
    const record = (item ?? {}) as Record<string, unknown>;
    const key = String(record.key ?? '').trim();
    const rawId = record.id ?? record.featureId ?? key;

    return {
        ...record,
        id: String(rawId),
        key,
        name: String(record.name ?? '').trim() || getBillingFeatureLabel({ key, name: '' }),
        dataType: parseBillingFeatureDataType(record.dataType),
        description: String(record.description ?? '').trim(),
        updatedAt: String(record.updatedAt ?? record.lastModifiedAt ?? nowIso()),
    };
}

function normalizePlanPrice(item: unknown): BillingPlanPrice {
    const record = (item ?? {}) as Record<string, unknown>;
    return {
        countryCode: String(record.countryCode ?? '').trim().toUpperCase(),
        priceAmount: Number(record.priceAmount ?? 0),
        isActive: Boolean(record.isActive ?? true),
    };
}

function normalizePlan(item: unknown): BillingPlan {
    const record = (item ?? {}) as Record<string, unknown>;
    const rawId = record.id ?? record.planId ?? record.name;

    return normalizeBillingPlan({
        ...record,
        id: String(rawId),
        name: String(record.name ?? '').trim(),
        description: String(record.description ?? '').trim(),
        imageUrl: resolveAdminAssetUrl(record.imageUrl ?? record.image_url),
        durationDays: Number(record.durationDays ?? 0),
        isActive: Boolean(record.isActive ?? true),
        prices: Array.isArray(record.prices) ? record.prices.map(normalizePlanPrice) : [],
        billingCountryCode: String(record.billingCountryCode ?? 'US').trim().toUpperCase(),
        billingPrice:
            typeof record.billingPrice === 'number' ? record.billingPrice : Number(record.billingPrice ?? record.basePriceUsd ?? 0),
        updatedAt: String(record.updatedAt ?? record.lastModifiedAt ?? nowIso()),
    });
}

function normalizeCountryCurrency(item: unknown): BillingCountryCurrency {
    const record = (item ?? {}) as Record<string, unknown>;
    const countryCode = String(record.countryCode ?? record.id ?? '').trim().toUpperCase();

    return {
        ...record,
        id: countryCode,
        countryCode,
        countryName: String(record.countryName ?? '').trim(),
        currency: String(record.currency ?? '').trim().toUpperCase(),
        isAirwallexSupported: Boolean(record.isAirwallexSupported),
        updatedAt: String(record.updatedAt ?? record.lastModifiedAt ?? nowIso()),
    };
}

function normalizePlanFeature(item: unknown, fallbackPlanId?: Identifier, fallbackFeatureId?: Identifier): BillingPlanFeature {
    const record = (item ?? {}) as Record<string, unknown>;
    const planId = record.planId ?? fallbackPlanId;
    const featureId = record.featureId ?? fallbackFeatureId;

    if (planId === undefined || featureId === undefined) {
        throw new Error('Plan feature record is missing planId or featureId');
    }

    const normalizedPlanId = typeof planId === 'number' ? planId : String(planId);
    const normalizedFeatureId = typeof featureId === 'number' ? featureId : String(featureId);

    return {
        ...record,
        id: String(record.id ?? `${normalizedPlanId}:${normalizedFeatureId}`),
        planId: normalizedPlanId,
        featureId: normalizedFeatureId,
        value: String(record.value ?? '').trim(),
        updatedAt: String(record.updatedAt ?? record.lastModifiedAt ?? nowIso()),
    };
}

function parsePlanFeatureCompositeId(id: Identifier): { planId: string; featureId: string } {
    const [planId, featureId] = String(id).split(':');
    if (!planId || !featureId) {
        throw new Error('Plan feature id is not in the expected planId:featureId format');
    }

    return { planId, featureId };
}

async function listPlansRaw(): Promise<BillingPlan[]> {
    const payload = await requestAdminSubscription<unknown>('plans', { method: 'GET' });
    return getArrayFromPayload<unknown>(payload).map(normalizePlan);
}

export async function getAdminUserActiveSubscription(userId: string): Promise<AdminUserActiveSubscription | null> {
    const normalizedUserId = String(userId).trim();
    if (!normalizedUserId) {
        throw new Error('User ID is required to load the active subscription');
    }

    const payload = await requestAdminSubscriptionOrNull<unknown>(
        `users/${encodeURIComponent(normalizedUserId)}/subscriptions/active`,
        { method: 'GET' },
    );

    if (payload === null || payload === undefined || payload === '') {
        return null;
    }

    const unwrapped = unwrapAdminApiPayload(payload as unknown);
    if (!unwrapped || typeof unwrapped !== 'object') {
        return null;
    }

    return normalizeAdminUserActiveSubscription(unwrapped);
}

export async function assignAdminIndividualSubscriptionToUser(userId: string, planId: Identifier): Promise<void> {
    const normalizedUserId = String(userId).trim();
    const normalizedPlanId = String(planId).trim();

    if (!normalizedUserId) {
        throw new Error('User ID is required to activate a subscription');
    }

    if (!normalizedPlanId) {
        throw new Error('Plan ID is required to activate a subscription');
    }

    await requestAdminSubscription<unknown>('users/subscriptions', {
        method: 'POST',
        body: JSON.stringify({
            userId: normalizedUserId,
            planId: normalizedPlanId,
        }),
    });
}

async function listPlanFeaturesByPlanId(planId: Identifier): Promise<BillingPlanFeature[]> {
    const payload = await requestAdminSubscription<unknown>('plan-features', { method: 'GET' }, { planId: String(planId) });
    return getArrayFromPayload<unknown>(payload).map((item) => normalizePlanFeature(item, planId));
}

export async function getAdminSubscriptionList(resource: AdminSubscriptionResource, params: any) {
    switch (resource) {
        case BILLING_FEATURES_RESOURCE: {
            const payload = await requestAdminSubscription<unknown>('features', { method: 'GET' });
            return sortAndPaginate(getArrayFromPayload<unknown>(payload).map(normalizeFeature), params);
        }
        case BILLING_PLANS_RESOURCE: {
            return sortAndPaginate(await listPlansRaw(), params);
        }
        case BILLING_COUNTRY_CURRENCIES_RESOURCE: {
            const filter = (params.filter ?? {}) as Record<string, unknown>;
            const payload = await requestAdminSubscription<unknown>('country-currencies', { method: 'GET' }, {
                ...(typeof filter.currency === 'string' && filter.currency.trim()
                    ? { currency: filter.currency.trim().toUpperCase() }
                    : {}),
            });
            return sortAndPaginate(getArrayFromPayload<unknown>(payload).map(normalizeCountryCurrency), params);
        }
        case BILLING_PLAN_FEATURES_RESOURCE: {
            const filter = (params.filter ?? {}) as Record<string, unknown>;
            const planIdFilter = filter.planId;
            const records =
                planIdFilter !== undefined && planIdFilter !== null && planIdFilter !== ''
                    ? await listPlanFeaturesByPlanId(planIdFilter as Identifier)
                    : (
                          await Promise.all((await listPlansRaw()).map((plan) => listPlanFeaturesByPlanId(plan.id)))
                      ).flat();

            return sortAndPaginate(records, params);
        }
    }
}

export async function getAdminSubscriptionOne(resource: AdminSubscriptionResource, params: any) {
    const listResult = await getAdminSubscriptionList(resource, {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'id', order: 'ASC' },
        filter: {},
    });
    const record = listResult.data.find((item) => item.id === params.id);

    if (!record) {
        throw new Error(`Record not found: ${resource}#${params.id}`);
    }

    return { data: record };
}

export async function getManyAdminSubscription(resource: AdminSubscriptionResource, params: any) {
    const listResult = await getAdminSubscriptionList(resource, {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'id', order: 'ASC' },
        filter: {},
    });

    const ids = new Set(params.ids);
    return { data: listResult.data.filter((item) => ids.has(item.id)) };
}

export async function createAdminSubscription(resource: AdminSubscriptionResource, params: any) {
    const data = (params.data ?? {}) as Record<string, unknown>;

    switch (resource) {
        case BILLING_FEATURES_RESOURCE: {
            const payload = await requestAdminSubscription<unknown>('features', {
                method: 'POST',
                body: JSON.stringify({
                    key: String(data.key ?? '').trim(),
                    dataType: parseBillingFeatureDataType(data.dataType),
                    description: String(data.description ?? '').trim(),
                }),
            });
            const unwrapped = unwrapAdminApiPayload(payload as unknown);
            return { data: unwrapped && typeof unwrapped === 'object' ? normalizeFeature(unwrapped) : normalizeFeature(data) };
        }
        case BILLING_PLANS_RESOURCE: {
            const payload = await requestAdminSubscription<unknown>('plans', {
                method: 'POST',
                body: JSON.stringify({
                    name: String(data.name ?? '').trim(),
                    description: String(data.description ?? '').trim(),
                    durationDays: Number(data.durationDays ?? 0),
                    isActive: Boolean(data.isActive),
                    prices: Array.isArray(data.prices) ? data.prices.map(normalizePlanPrice) : [],
                }),
            });
            const unwrapped = unwrapAdminApiPayload(payload as unknown);
            return { data: unwrapped && typeof unwrapped === 'object' ? normalizePlan(unwrapped) : normalizePlan(data) };
        }
        case BILLING_COUNTRY_CURRENCIES_RESOURCE: {
            const payload = await requestAdminSubscription<unknown>('country-currencies', {
                method: 'POST',
                body: JSON.stringify({
                    countryCode: String(data.countryCode ?? '').trim().toUpperCase(),
                    countryName: String(data.countryName ?? '').trim(),
                    currency: String(data.currency ?? '').trim().toUpperCase(),
                    isAirwallexSupported: Boolean(data.isAirwallexSupported),
                }),
            });
            const unwrapped = unwrapAdminApiPayload(payload as unknown);
            return {
                data:
                    unwrapped && typeof unwrapped === 'object'
                        ? normalizeCountryCurrency(unwrapped)
                        : normalizeCountryCurrency(data),
            };
        }
        case BILLING_PLAN_FEATURES_RESOURCE: {
            const payload = await requestAdminSubscription<unknown>('plan-features', {
                method: 'POST',
                body: JSON.stringify({
                    planId: data.planId,
                    featureId: data.featureId,
                    value: String(data.value ?? '').trim(),
                }),
            });
            const unwrapped = unwrapAdminApiPayload(payload as unknown);
            return {
                data:
                    unwrapped && typeof unwrapped === 'object'
                        ? normalizePlanFeature(unwrapped, data.planId as Identifier, data.featureId as Identifier)
                        : normalizePlanFeature(data, data.planId as Identifier, data.featureId as Identifier),
            };
        }
    }
}

export async function updateAdminSubscription(resource: AdminSubscriptionResource, params: any) {
    const data = (params.data ?? {}) as Record<string, unknown>;
    const previousData = (params.previousData ?? {}) as Record<string, unknown>;

    switch (resource) {
        case BILLING_FEATURES_RESOURCE: {
            const payload = await requestAdminSubscription<unknown>('features', {
                method: 'POST',
                body: JSON.stringify({
                    featureId: params.id,
                    key: String(data.key ?? '').trim(),
                    dataType: parseBillingFeatureDataType(data.dataType),
                    description: String(data.description ?? '').trim(),
                }),
            });
            const unwrapped = unwrapAdminApiPayload(payload as unknown);
            return {
                data:
                    unwrapped && typeof unwrapped === 'object'
                        ? normalizeFeature(unwrapped)
                        : normalizeFeature({ ...previousData, ...data, id: params.id }),
            };
        }
        case BILLING_PLANS_RESOURCE: {
            const payload = await requestAdminSubscription<unknown>('plans', {
                method: 'POST',
                body: JSON.stringify({
                    planId: params.id,
                    name: String(data.name ?? '').trim(),
                    description: String(data.description ?? '').trim(),
                    durationDays: Number(data.durationDays ?? 0),
                    isActive: Boolean(data.isActive),
                    prices: Array.isArray(data.prices) ? data.prices.map(normalizePlanPrice) : [],
                }),
            });
            const unwrapped = unwrapAdminApiPayload(payload as unknown);
            return {
                data:
                    unwrapped && typeof unwrapped === 'object'
                        ? normalizePlan(unwrapped)
                        : normalizePlan({ ...previousData, ...data, id: params.id }),
            };
        }
        case BILLING_COUNTRY_CURRENCIES_RESOURCE: {
            const nextCountryCode = String(data.countryCode ?? params.id ?? '').trim().toUpperCase();
            const previousCountryCode = String(previousData.countryCode ?? params.id ?? '').trim().toUpperCase();
            const payload = await requestAdminSubscription<unknown>('country-currencies', {
                method: 'POST',
                body: JSON.stringify({
                    countryCode: nextCountryCode,
                    countryName: String(data.countryName ?? '').trim(),
                    currency: String(data.currency ?? '').trim().toUpperCase(),
                    isAirwallexSupported: Boolean(data.isAirwallexSupported),
                }),
            });

            if (previousCountryCode && previousCountryCode !== nextCountryCode) {
                await requestAdminSubscription<unknown>(`country-currencies/${previousCountryCode}`, {
                    method: 'DELETE',
                });
            }

            const unwrapped = unwrapAdminApiPayload(payload as unknown);
            return {
                data:
                    unwrapped && typeof unwrapped === 'object'
                        ? normalizeCountryCurrency(unwrapped)
                        : normalizeCountryCurrency({ ...previousData, ...data, id: nextCountryCode }),
            };
        }
        case BILLING_PLAN_FEATURES_RESOURCE: {
            const nextPlanId = data.planId as Identifier;
            const nextFeatureId = data.featureId as Identifier;
            const previousPlanId = previousData.planId as Identifier;
            const previousFeatureId = previousData.featureId as Identifier;
            const payload = await requestAdminSubscription<unknown>('plan-features', {
                method: 'POST',
                body: JSON.stringify({
                    planId: nextPlanId,
                    featureId: nextFeatureId,
                    value: String(data.value ?? '').trim(),
                }),
            });

            if (
                previousPlanId !== undefined &&
                previousFeatureId !== undefined &&
                (String(previousPlanId) !== String(nextPlanId) || String(previousFeatureId) !== String(nextFeatureId))
            ) {
                await requestAdminSubscription<unknown>(
                    `plan-features/${String(previousPlanId)}/${String(previousFeatureId)}`,
                    { method: 'DELETE' },
                );
            }

            const unwrapped = unwrapAdminApiPayload(payload as unknown);
            return {
                data:
                    unwrapped && typeof unwrapped === 'object'
                        ? normalizePlanFeature(unwrapped, nextPlanId, nextFeatureId)
                        : normalizePlanFeature({ ...previousData, ...data }, nextPlanId, nextFeatureId),
            };
        }
    }
}

export async function deleteAdminSubscription(resource: AdminSubscriptionResource, params: any) {
    switch (resource) {
        case BILLING_FEATURES_RESOURCE:
            await requestAdminSubscription<unknown>(`features/${String(params.id)}`, { method: 'DELETE' });
            return { data: normalizeFeature(params.previousData ?? { id: params.id }) };
        case BILLING_PLANS_RESOURCE:
            await requestAdminSubscription<unknown>(`plans/${String(params.id)}`, { method: 'DELETE' });
            return { data: normalizePlan(params.previousData ?? { id: params.id }) };
        case BILLING_COUNTRY_CURRENCIES_RESOURCE: {
            const countryCode = String(params.previousData?.countryCode ?? params.id ?? '').trim().toUpperCase();
            await requestAdminSubscription<unknown>(`country-currencies/${countryCode}`, { method: 'DELETE' });
            return { data: normalizeCountryCurrency(params.previousData ?? { id: countryCode, countryCode }) };
        }
        case BILLING_PLAN_FEATURES_RESOURCE: {
            const previousData = params.previousData as BillingPlanFeature | undefined;
            const composite = previousData
                ? { planId: String(previousData.planId), featureId: String(previousData.featureId) }
                : parsePlanFeatureCompositeId(params.id);
            await requestAdminSubscription<unknown>(`plan-features/${composite.planId}/${composite.featureId}`, {
                method: 'DELETE',
            });
            return {
                data: normalizePlanFeature(
                    previousData ?? { id: params.id, planId: composite.planId, featureId: composite.featureId },
                    composite.planId,
                    composite.featureId,
                ),
            };
        }
    }
}

function normalizePricingPromotionResult(payload: unknown): AdminPricingPromotionResult {
    const unwrapped = unwrapAdminApiPayload(payload as AdminPricingPromotionResult | AdminApiEnvelope<AdminPricingPromotionResult>);
    const result = (unwrapped && typeof unwrapped === 'object' ? unwrapped : {}) as Partial<AdminPricingPromotionResult>;
    return {
        sourceEnvironment: String(result.sourceEnvironment ?? ''),
        targetEnvironment: String(result.targetEnvironment ?? ''),
        canPublish: result.canPublish === true,
        published: result.published === true,
        publishedAtUtc: result.publishedAtUtc ?? null,
        blockers: Array.isArray(result.blockers) ? result.blockers.map(String) : [],
        warnings: Array.isArray(result.warnings) ? result.warnings.map(String) : [],
        changes: result.changes ?? {},
        planImagesToCopy: Number(result.planImagesToCopy ?? 0),
        planImagesCopied: Number(result.planImagesCopied ?? 0),
    };
}

export async function validateAdminPricingPublish(): Promise<AdminPricingPromotionResult> {
    const payload = await requestAdminSubscription<unknown>('pricing/validate-publish', { method: 'POST' });
    return normalizePricingPromotionResult(payload);
}

export async function publishAdminPricingToTarget(): Promise<AdminPricingPromotionResult> {
    const payload = await requestAdminSubscription<unknown>('pricing/publish-target', { method: 'POST' });
    return normalizePricingPromotionResult(payload);
}
